/* =======================================================================
   Knowledge Base — interactive walkthrough. Azure item 416, phase 3.

   "Take me there" in an article hands the reader to the real screen and
   talks them through it: the page dims, a rounded hole is left around the
   one control that matters, and a tooltip points at it. When the reader
   does the thing, it moves on by itself. At the end the contrast comes back
   and a small badge stays in the header as the way back in.

   Ported from the mockup on 10 Sep 2026. The engine is deliberately kept
   imperative and DOM-driven (refs, not React state, for the overlay
   geometry): it is the tested code, and geometry that re-renders through
   React on every scroll would be both slower and harder to reason about.

   Two decisions worth keeping if this grows:

   * Steps advance off STATE, not off clicks. Each step owns a done()
     predicate read every 100ms, so the tour cannot fall out of step with
     the app and needs no hooks inside the app's click handlers. It also
     makes Resume land on the first thing still to do.

   * Targets are data-tour="…" attributes on the real Work Orders screen
     (BranchBar, CreateButton, WO.js, createwomodal.js, WoTable). Restyle
     freely; the tour keeps working. The app exposes two things the tour
     cannot read from the URL or DOM through kbSession.js: how many work
     orders have been created this session, and whether a tour is running.
   ======================================================================= */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KB_BASE, KB_WALK_START, SESSION, announce, woCreatedCount, woLastCreatedId, setWalkthroughRunning, setLastArticle, readJson, writeJson } from './kbSession';
import './kb.css';

/* ---------------- what the real app tells us ---------------- */
const qs = () => new URLSearchParams(window.location.search);
const onWorkList = () => window.location.pathname === '/work';
const branchPicked = () => onWorkList() && Number(qs().get('branch') || 0) > 0;
const dialogOpen = () => onWorkList() && qs().get('create') === '1';
const searching = () => onWorkList() && !!(qs().get('keyword') || '').trim();
const filtered = () => onWorkList() && !!qs().get('criteria');
const filterOpen = () => onWorkList() && !!document.querySelector('[data-tour="wo-filter-panel"]');
const filled = key => !!document.querySelector('[data-tour="' + key + '"][data-tour-filled]');
/* a step's target is a selector, or a function returning one — the finish step
   points at the row that was just created, whose id is not known in advance */
const sel = s => (typeof s.target === 'function' ? s.target() : s.target);

/* ---------------- the walkthroughs ---------------- */
/* Has the reader created a work order since this walkthrough was last set
   up? Submitting clears the form, so the form cannot answer it; the session
   counter only ever goes up, so a mark taken at reset() survives everything
   except deliberately starting again. */
let wtMark = 0;
const wtMadeOne = () => woCreatedCount() > wtMark;
/* Marks for the find walkthrough, set by its own done() predicates: once the
   reader has searched, that stays true even after they clear the search —
   which is the next step. Cleared by its reset(). */
const seen = { search: false };

const WALKTHROUGHS = {
  'create-work-order': {
    label: 'Create a work order',
    /* The screen the article's first step assumes: the list, on All, nothing
       open. Work orders already created are left alone. */
    reset(navigate){
      wtMark = woCreatedCount();
      navigate('/work?branch=0', { replace: true });
    },
    /* Work in progress worth not throwing away: the dialog open, or a
       required field already chosen. A branch on its own does not count —
       reselecting one is the walkthrough's own first step — and neither does
       a finished job, or the demo could never be run from the top twice. */
    midTask(){ return dialogOpen() || filled('wo-customer') || filled('wo-service'); },
    steps: [
      { kind: 'intro',
        title: 'Let’s create a work order',
        body: '<p>You’re on the <b>Work Orders</b> tab. There are four things to do: pick a branch, ' +
              'open the form, fill in the two required fields, and submit.</p>' +
              '<p>I’ll point at each one. Do it for real — this is the screen, not a picture of it.</p>' },

      { target: '[data-tour="wo-branches"]', place: 'bottom',
        title: 'Pick the branch first',
        body: '<p>A work order belongs to one branch, and its number comes from that branch’s prefix. ' +
              'You cannot change it afterwards.</p>' +
              '<p>Look at the top right: on <span class="k">All</span> there is no <span class="k">+ Create</span> button at all. ' +
              'That is why this step comes first.</p>',
        doIt: 'Click the branch the job is on',
        done: () => branchPicked() || wtMadeOne() },

      { target: '[data-tour="wo-create"]', place: 'bottom',
        title: 'Now + Create is there',
        body: '<p>It appeared the moment a single branch was selected — along with ' +
              '<span class="k">Filter</span>.</p>',
        doIt: 'Click <b>+ Create</b>',
        done: () => dialogOpen() || wtMadeOne() },

      { target: '[data-tour="wo-customer"]', also: '[data-tour="wo-customer"] ul', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Choose the customer',
        body: '<p>Required. The customer has to exist in Makani already — if it does not, you have to ' +
              'add it from the Customers tab first and come back.</p>' +
              '<p>There are a lot of them, so type to narrow the list.</p>',
        doIt: 'Open the list and pick a customer',
        done: () => filled('wo-customer') || wtMadeOne() },

      { target: '[data-tour="wo-service"]', also: '[data-tour="wo-service"] ul', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Choose the type of service',
        body: '<p>Also required. Service Call, Quoted Job, PM, Parts Only, Compressor Rebuild or Admin Hours.</p>' +
              '<p>If the one you need is missing, <span class="k">+ Add Service Type</span> beside the label adds it.</p>',
        doIt: 'Pick a type of service',
        done: () => filled('wo-service') || wtMadeOne() },

      { target: '[data-tour="wo-optional-start"]', targetEnd: '[data-tour="wo-description"]', also: '[data-tour="wo-dialog"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'The rest is optional',
        body: '<p>Everything between <b>NTE</b> and <b>Description</b> can be left exactly as it is. ' +
              'Fill in whatever you already know:</p>' +
              '<ul>' +
              '<li><b>NTE</b> — the not-to-exceed dollar amount. Defaults to $0.</li>' +
              '<li><b>Start Date</b> — defaults to today.</li>' +
              '<li><b>Equipment</b> — the unit being worked on.</li>' +
              '<li><b>Customer Purchase Order #</b> — the customer’s PO number, if they gave you one.</li>' +
              '<li><b>Assign Primary Technician</b> — who is taking the job. Safe to leave blank now and ' +
                  'set later, though the job does not reach anyone until someone is assigned.</li>' +
              '<li><b>Description</b> — what is wrong.</li>' +
              '</ul>',
        done: () => wtMadeOne() },

      { target: '[data-tour="wo-description"]', place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'One of them is worth your time',
        body: '<p>The description is what the technician reads first, so be specific.</p>' +
              '<p>“Walk-in freezer running at 30 degrees” beats “freezer issue”.</p>',
        done: () => wtMadeOne() },

      { target: '[data-tour="wo-submit"]', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Submit',
        body: '<p>Notice it was greyed out until both required fields were set. That is the only thing ' +
              'stopping you — nothing else on the form is checked.</p>',
        doIt: 'Click <b>SUBMIT</b>',
        done: () => wtMadeOne() },

      { kind: 'finish', place: 'bottom',
        /* The row that was just created, by id, wherever the list puts it — the
           create succeeds before the list refetches, so for a moment the row is
           not there at all; until it is, the card sits centred with no ring, and
           the watcher lights the row the instant it appears. */
        target: () => (woLastCreatedId() ? '#wo-row-' + woLastCreatedId() : '[data-tour="wo-row"]'),
        /* The table scrolls sideways and Status is the column that falls off —
           the one thing this card is about. Bring it into view first. */
        prep(){
          const row = document.querySelector(woLastCreatedId() ? '#wo-row-' + woLastCreatedId() : '[data-tour="wo-row"]');
          if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center' });
          let p = row && row.parentElement;
          while (p && p !== document.body){ if (p.scrollWidth > p.clientWidth + 2){ p.scrollLeft = p.scrollWidth; break; } p = p.parentElement; }
        },
        title: 'That’s a work order',
        body: '<p>It is at the top of the list in <b>Open</b> status — not Requested — with its number ' +
              'assigned from the branch prefix.</p>' +
              '<p>It has no priority, because the browser cannot set one, and no History yet: creating a ' +
              'work order is not itself logged. Both are normal.</p>' }
    ]
  },

  'find-work-orders': {
    label: 'Find a work order',
    /* Nothing here is written to the database, so reset is just the clean
       list: All, no search, no filter. The marks make each step stick once
       done — clearing the search is the walkthrough's own step, so "you
       searched" must survive the search being cleared. */
    reset(navigate){
      seen.search = false;
      navigate('/work?branch=0', { replace: true });
    },
    midTask(){ return searching() || filterOpen() || filtered(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s find a work order',
        body: '<p>You’re on the <b>Work Orders</b> tab. Three tools do the work: <b>Search</b> at the top, ' +
              'the column headings for sorting, and <b>Filter</b> for anything narrower.</p>' +
              '<p>I’ll point at each one. Do it for real — this is the live list.</p>' },

      { target: '[data-tour="wo-search"]', place: 'bottom',
        title: 'Search, from anywhere',
        body: '<p>This box searches the work order number, the customer and the description at once, ' +
              'so a job number, a customer name or a word like <i>freezer</i> all work.</p>' +
              '<p>The list narrows as you type and the match is highlighted in yellow.</p>',
        doIt: 'Type something — a customer name is a good start',
        done: () => { if (searching()) seen.search = true; return seen.search; } },

      { target: '[data-tour="wo-search-clear"]', place: 'bottom',
        title: 'Clear it the same way',
        body: '<p>The ⊗ inside the box clears the search and brings the whole list back. ' +
              'A search that is still in place is the usual reason a work order “is missing”.</p>',
        doIt: 'Click the ⊗',
        /* "no search in place" — so a reader who skipped the search step is
           not stuck here, and one who cleared it moves on the moment they do */
        done: () => !searching() },

      { target: '[data-tour="wo-headers"]', place: 'bottom', kind: 'info',
        title: 'Sort by any column',
        body: '<p>Click a heading to sort by it — an arrow shows the direction, and a second click reverses it. ' +
              '<b>WO#</b> and <b>Date Started</b> are the two people use most.</p>' +
              '<p>Try it now if you like; the list stays sorted while you carry on.</p>',
        /* an info tile still needs a way to be "past": a reader who has raced
           ahead to the filter should not be pulled back to read about sorting */
        done: () => filterOpen() || filtered() },

      { target: '[data-tour="wo-branches"]', place: 'bottom',
        title: 'Filter needs a branch',
        body: '<p>Look at the top right: on <span class="k">All</span> there is no <span class="k">Filter</span> button. ' +
              'Filtering only works inside a single branch.</p>',
        doIt: 'Click a branch tab',
        done: () => branchPicked() || filterOpen() || filtered() },

      { target: '[data-tour="wo-filter"]', place: 'bottom',
        title: 'Now Filter is there',
        body: '<p>It appeared with the branch — and so did <span class="k">Download</span>, which exports ' +
              'whatever the filter finds as an Excel file.</p>',
        doIt: 'Click <b>Filter</b>',
        done: () => filterOpen() || filtered() },

      { target: '[data-tour="wo-filter-panel"]', place: 'bottom', kind: 'info',
        title: 'Seven ways to narrow it',
        body: '<p><b>Service Type</b>, <b>Customer</b>, <b>Status</b>, <b>Technician</b>, <b>Start date</b>, ' +
              '<b>End date</b> and <b>Quoted by</b>. Any combination.</p>' +
              '<p>Set one now — <b>by Status</b> is the quickest to try.</p>',
        done: () => filtered() },

      { target: '[data-tour="wo-filter-apply"]', place: 'left',
        title: 'Apply',
        body: '<p>Nothing changes until you click it. The filter is written into the page address, so the ' +
              'browser’s Back button undoes it and a reload keeps it.</p>',
        doIt: 'Click <b>Apply</b>',
        done: () => filtered() },

      { kind: 'finish', place: 'left', target: '[data-tour="wo-filter-clear"]',
        title: 'That’s the filtered list',
        body: '<p>Two things to remember. <b>Clear</b>, right here, resets every field at once — the filter ' +
              'otherwise stays on while you move between branches.</p>' +
              '<p>And the list loads in batches: if a job seems to be missing, click <b>LOAD MORE</b> at the ' +
              'bottom before deciding it does not exist.</p>' }
    ]
  }
};

/* ---------------- badge state (read by header.js) ---------------- */
export const wtBadgeState = () => SESSION.getItem('makaniKbWtBadge') || '';   /* '' | 'done' | 'paused' */
export const wtBadgeSlug = () => SESSION.getItem('makaniKbWtSlug') || '';
const setBadge = (state, slug) => {
  SESSION.setItem('makaniKbWtBadge', state || '');
  if (slug) SESSION.setItem('makaniKbWtSlug', slug);
  announce();
};

/* ---------------- the component ---------------- */
export default function KbWalkthrough(){
  const navigate = useNavigate();
  const location = useLocation();
  const root = useRef(null);
  const eng = useRef(null);

  /* The engine lives in a ref so its geometry work never goes through
     React. It is created once and given the latest navigate each render. */
  if (!eng.current) eng.current = createEngine();
  eng.current.navigate = navigate;

  useEffect(() => {
    const e = eng.current;
    e.mount(root.current);
    const onStart = ev => e.start(ev.detail.slug, 'auto');
    const onResume = ev => e.start(ev.detail.slug, ev.detail.resume);
    const onKey = ev => { if (e.run && ev.key === 'Escape'){ ev.preventDefault(); e.end('paused'); } };
    window.addEventListener(KB_WALK_START, onStart);
    window.addEventListener('makani-kb-walk-resume', onResume);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(KB_WALK_START, onStart);
      window.removeEventListener('makani-kb-walk-resume', onResume);
      document.removeEventListener('keydown', onKey);
      e.unmount();
    };
  }, []);

  /* A route change while running: the target may have moved or vanished. */
  useEffect(() => { if (eng.current.run) eng.current.paint(); }, [location.pathname, location.search]);

  return (
    <div className="kb-wt" ref={root}>
      <div className="wt" data-kbwt="root">
        <div className="wt-dim" />
        <div className="wt-ring hide" />
        <div className="wt-tip" />
      </div>
    </div>
  );
}

/* ---------------- the engine ---------------- */
function createEngine(){
  const E = {
    run: null, slug: null, step: 0, prepped: -1, navigate: null,
    els: null, watch: 0, repaintQueued: false,
  };

  E.mount = root => {
    E.els = {
      wt: root.querySelector('.wt'), dim: root.querySelector('.wt-dim'),
      ring: root.querySelector('.wt-ring'), tip: root.querySelector('.wt-tip')
    };
    E.els.wt.addEventListener('click', E.onClick);
  };
  E.unmount = () => { clearInterval(E.watch); if (E.els) E.els.wt.removeEventListener('click', E.onClick); };

  const stepDef = () => (E.run ? E.run.steps[E.step] : null);

  /* ---- running ---- */
  E.start = (slug, resume) => {
    const w = WALKTHROUGHS[slug];
    if (!w) return;
    if (resume === 'auto') resume = !!(w.midTask && w.midTask());
    E.run = w; E.slug = slug; E.step = 0; E.prepped = -1;
    setBadge('', slug);
    setLastArticle(slug); announce();          /* the article rides along in the drawer */
    setWalkthroughRunning(true);
    /* The drawer is dimmed and unreadable while a walkthrough runs, and at a
       laptop width it takes the room the tooltip needs beside the dialog
       (sidebar 257 + dialog 436 + gap + tooltip 330). When that is the case,
       tuck it away for the duration and put it back afterwards; on a wide
       screen it stays where it is. */
    const dw = readJson('makaniKbDrawer', { dismissed: false, hidden: false, width: 400 });
    const roomBeside = window.innerWidth - 257 - (dw.dismissed ? 0 : Math.min(dw.width, 760)) - 436 - 14 - 330 - 24;
    E.drawerWas = null;
    const showing = !dw.dismissed || !!document.querySelector('.kb.drawer.on');
    if (showing && roomBeside < 0){ E.drawerWas = { ...dw, dismissed: false }; writeJson('makaniKbDrawer', { ...dw, dismissed: true }); announce(); }
    if (!resume && w.reset) w.reset(E.navigate);
    else if (!onWorkList()) E.navigate('/work?branch=0', { replace: true });
    E.els.wt.classList.add('on');
    E.startWatch();
    /* the page may be mid-navigation; paint once the DOM has caught up */
    setTimeout(() => {
      if (!E.run) return;
      if (resume) E.step = E.landingStep();
      E.paint();
    }, 60);
  };

  E.end = how => {
    setBadge(how, E.slug);
    E.run = null; E.step = 0;
    E.els.wt.classList.remove('on');
    E.stopWatch();
    setWalkthroughRunning(false);
    if (E.drawerWas){ writeJson('makaniKbDrawer', { ...E.drawerWas, dismissed: false }); E.drawerWas = null; announce(); }
  };

  E.next = () => {
    if (!E.run) return;
    if (E.step >= E.run.steps.length - 1){ E.end('done'); return; }
    E.step += 1; E.paint();
  };
  E.back = () => { if (E.run && E.step > 0){ E.step -= 1; E.paint(); } };

  /* Where a resume should pick the reader up: the first step still to do,
     walking back to the last one whose target is actually on screen. */
  E.landingStep = () => {
    const steps = E.run.steps;
    let i = (steps[0].kind === 'intro' && steps.length > 1) ? 1 : 0;
    while (i < steps.length - 1 && steps[i].done && steps[i].done()) i++;
    const onScreen = n => !sel(steps[n]) || !!document.querySelector(sel(steps[n]));
    while (i > 0 && !onScreen(i)) i--;
    return i;
  };

  /* Advances past every step whose done() already holds. A timer, not
     requestAnimationFrame, because rAF does not tick in every embedded
     browser and a watchdog that silently never runs is worse than none.
     Also repaints when the target's geometry changes, which covers the
     dialog scrolling and the table scrolling sideways. */
  let last = '';
  E.startWatch = () => {
    clearInterval(E.watch);
    E.watch = setInterval(() => {
      if (!E.run){ E.stopWatch(); return; }
      let moved = false;
      while (E.step < E.run.steps.length - 1){
        const s = E.run.steps[E.step];
        if (s.done && s.done()){ E.step += 1; moved = true; } else break;
      }
      const b = E.rect();
      const key = (b ? [b.x, b.y, b.w, b.h].map(Math.round).join(',') : 'none') + '|' + E.step;
      if (moved || key !== last){ last = key; E.paint(false, true); }
    }, 100);
  };
  E.stopWatch = () => { clearInterval(E.watch); E.watch = 0; };

  /* ---- geometry ---- */
  const union = (a, b) => {
    if (!a) return b; if (!b) return a;
    const x1 = Math.min(a.x, b.x), y1 = Math.min(a.y, b.y);
    const x2 = Math.max(a.x + a.w, b.x + b.w), y2 = Math.max(a.y + a.h, b.y + b.h);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  };
  const boxOf = el => { const r = el.getBoundingClientRect(); return { x: r.left, y: r.top, w: r.width, h: r.height }; };

  E.rect = () => {
    const s = stepDef();
    if (!s || !sel(s)) return null;
    const el = document.querySelector(sel(s));
    if (!el) return null;
    let box = boxOf(el);
    if (s.targetEnd){ const e2 = document.querySelector(s.targetEnd); if (e2) box = union(box, boxOf(e2)); }
    /* an open picker hangs outside its trigger; the hole is the union */
    if (s.also){ const ex = document.querySelector(s.also); if (ex) box = union(box, boxOf(ex)); }
    /* clip to whatever scrolls the target — a row wider than its table
       would otherwise light up empty space under the app's own menu */
    let p = el.parentElement;
    while (p && p !== document.body){
      const cs = getComputedStyle(p);
      if (/auto|scroll|hidden/.test(cs.overflowX + ' ' + cs.overflowY)){
        const c = p.getBoundingClientRect();
        const x1 = Math.max(box.x, c.left), y1 = Math.max(box.y, c.top);
        const x2 = Math.min(box.x + box.w, c.right), y2 = Math.min(box.y + box.h, c.bottom);
        box = { x: x1, y: y1, w: Math.max(0, x2 - x1), h: Math.max(0, y2 - y1) };
      }
      p = p.parentElement;
    }
    return box;
  };

  /* ---- painting ---- */
  E.paint = (scrollIntoView, isFollowUp) => {
    if (!E.run) return;
    const s = stepDef();
    /* prep runs once per step — but not before the step's target exists, or the
       finish step would try to scroll to a row the list has not fetched yet */
    if (E.prepped !== E.step && (!sel(s) || document.querySelector(sel(s)))){ E.prepped = E.step; if (s.prep) s.prep(); }
    const pad = 6, WT_R = 9;
    const box = E.rect();
    const W = window.innerWidth, H = window.innerHeight;
    const px = n => n + 'px';
    const { dim, ring, tip } = E.els;

    if (!box){
      dim.style.clipPath = 'none';
      ring.classList.add('hide');
    } else {
      const x = box.x - pad, y = box.y - pad, w = box.w + pad * 2, h = box.h + pad * 2;
      const r = Math.max(0, Math.min(WT_R, w / 2, h / 2));
      const d = dim.getBoundingClientRect();
      const X = x - d.left, Y = y - d.top;
      const n = v => Math.round(v * 100) / 100;
      const outer = 'M0 0 H' + n(d.width) + ' V' + n(d.height) + ' H0 Z';
      const hole =
        'M' + n(X + r) + ' ' + n(Y) +
        ' H' + n(X + w - r) + ' A' + n(r) + ' ' + n(r) + ' 0 0 1 ' + n(X + w) + ' ' + n(Y + r) +
        ' V' + n(Y + h - r) + ' A' + n(r) + ' ' + n(r) + ' 0 0 1 ' + n(X + w - r) + ' ' + n(Y + h) +
        ' H' + n(X + r) + ' A' + n(r) + ' ' + n(r) + ' 0 0 1 ' + n(X) + ' ' + n(Y + h - r) +
        ' V' + n(Y + r) + ' A' + n(r) + ' ' + n(r) + ' 0 0 1 ' + n(X + r) + ' ' + n(Y) + ' Z';
      dim.style.clipPath = 'path(evenodd, "' + outer + ' ' + hole + '")';
      ring.classList.remove('hide');
      ring.style.left = px(x); ring.style.top = px(y); ring.style.width = px(w); ring.style.height = px(h);
    }
    /* pulse only where a click is being asked for */
    ring.classList.toggle('live', !!(box && s.doIt));

    /* ---- the tooltip ---- */
    const total = E.run.steps.length, finish = s.kind === 'finish';
    let h = finish
      ? '<div class="wt-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 12.5l5.5 5.5L20 7"/></svg></div>'
      : '<div class="wt-count">Step ' + (E.step + 1) + ' of ' + total + ' · ' + E.run.label + '</div>';
    h += '<h4>' + s.title + '</h4>' + s.body;
    if (s.doIt) h += '<div class="wt-do"><span class="dot"></span><span>' + s.doIt + ' to carry on</span></div>';
    h += '<div class="wt-foot">';
    if (finish){
      h += '<button class="wt-btn quiet" data-wt="article" type="button">Back to the article</button>' +
           '<span class="sp"></span><button class="wt-btn go" data-wt="close" type="button">Done</button>';
    } else {
      h += '<div class="wt-dots">' + E.run.steps.map((_, i) =>
        '<i class="' + (i === E.step ? 'on' : (i < E.step ? 'past' : '')) + '"></i>').join('') + '</div><span class="sp"></span>';
      if (E.step > 0) h += '<button class="wt-btn quiet" data-wt="back" type="button">Back</button>';
      if (s.kind === 'intro') h += '<button class="wt-btn go" data-wt="next" type="button">Start</button>';
      else if (s.kind === 'info' || !s.doIt) h += '<button class="wt-btn go" data-wt="next" type="button">Next</button>';
      else h += '<button class="wt-btn line" data-wt="next" type="button">Skip</button>';
      h += '<button class="wt-btn quiet" data-wt="close" type="button" title="Leave the walkthrough">Exit</button>';
    }
    h += '</div>';
    tip.innerHTML = h;
    tip.classList.toggle('center', !box);
    tip.classList.toggle('finish', !!finish);

    /* ---- place it: preference, then whatever fits, measured from the
       obstacle (the dialog) rather than the target, so an inset target
       cannot be "cleared" by four pixels ---- */
    const tw = tip.offsetWidth, th = tip.offsetHeight, gap = 14, edge = 12;
    let tx, ty;
    if (!box){ tx = (W - tw) / 2; ty = Math.max(edge, (H - th) / 2); }
    else {
      const keep = s.avoid ? document.querySelector(s.avoid) : null;
      const kr = keep ? keep.getBoundingClientRect() : null;
      const R = kr ? Math.max(box.x + box.w, kr.right) : box.x + box.w;
      const L = kr ? Math.min(box.x, kr.left) : box.x;
      const B = kr ? Math.max(box.y + box.h, kr.bottom) : box.y + box.h;
      const T = kr ? Math.min(box.y, kr.top) : box.y;
      const fits = { bottom: B + gap + th < H - edge, top: T - gap - th > edge, left: L - gap - tw > edge, right: R + gap + tw < W - edge };
      const at = p => {
        let x, y;
        if (p === 'bottom'){ x = box.x + box.w / 2 - tw / 2; y = B + gap; }
        else if (p === 'top'){ x = box.x + box.w / 2 - tw / 2; y = T - gap - th; }
        else if (p === 'left'){ x = L - gap - tw; y = box.y + box.h / 2 - th / 2; }
        else { x = R + gap; y = box.y + box.h / 2 - th / 2; }
        return { x: Math.min(Math.max(edge, x), W - tw - edge), y: Math.min(Math.max(edge, y), H - th - edge) };
      };
      const order = [s.place || 'bottom', 'bottom', 'top', 'right', 'left'];
      const use = order.find(p => fits[p]) || 'bottom';
      let a = at(use);
      /* Whatever won, it must not sit on the control the step is asking for.
         At a narrow window every placement can overlap the dialog, and the
         fallback then lands on the target itself — on the Submit step that
         covered the SUBMIT button. Shift to whichever side of the target has
         more room; covering fields already filled in is an acceptable cost. */
      const tb = { x: box.x - pad, y: box.y - pad, w: box.w + pad * 2, h: box.h + pad * 2 };
      const overlapsTarget = () => !(a.x + tw <= tb.x || a.x >= tb.x + tb.w || a.y + th <= tb.y || a.y >= tb.y + tb.h);
      if (overlapsTarget()){
        const above = tb.y, below = H - (tb.y + tb.h);
        const y = above >= below ? tb.y - gap - th : tb.y + tb.h + gap;
        a = { x: a.x, y: Math.min(Math.max(edge, y), H - th - edge) };
      }
      tx = a.x; ty = a.y;
    }
    tip.style.left = tx + 'px'; tip.style.top = ty + 'px';

    if (scrollIntoView && box){
      const el = document.querySelector(sel(s));
      if (el && (box.y < 0 || box.y + box.h > H)) el.scrollIntoView({ block: 'center' });
    }
    /* one corrective pass after the current task — layout has not always
       settled when this runs from inside a re-render */
    if (!isFollowUp && !E.repaintQueued){
      E.repaintQueued = true;
      setTimeout(() => { E.repaintQueued = false; if (E.run) E.paint(false, true); });
    }
  };

  /* ---- clicks on the overlay ---- */
  E.onClick = e => {
    const act = e.target.closest && e.target.closest('[data-wt]');
    if (act){
      e.preventDefault(); e.stopPropagation();
      const a = act.dataset.wt;
      if (a === 'next') E.next();
      else if (a === 'back') E.back();
      else if (a === 'close') E.end(stepDef() && stepDef().kind === 'finish' ? 'done' : 'paused');
      else if (a === 'article'){ const slug = E.slug; E.end('done'); E.navigate(KB_BASE + '/a/' + encodeURIComponent(slug)); }
      return;
    }
    /* a click on the dim is a click on the wrong thing — say so */
    if (E.run && e.target.classList.contains('wt-dim')){
      const ring = E.els.ring;
      ring.classList.remove('nudge'); void ring.offsetWidth; ring.classList.add('nudge');
      ring.addEventListener('animationend', () => ring.classList.remove('nudge'), { once: true });
    }
  };

  return E;
}
